import { Add, Save } from '@mui/icons-material';
import { Box, Grid } from '@mui/material';
import {
  checkSIIDocumentForUploadingInvoices,
  getBusinessNameByIdentifier,
} from 'lib/api/sii';
import {
  DateInput,
  MoneyInput,
  SelectInput,
  TextInput,
} from 'lib/common/Inputs';
import CustomButton from 'lib/designSystem/Button';
import { showAlert, validaRut } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import type {
  DocumentImporterConfig,
  ImporterDocumentRow,
  ManualFormFieldConfig,
} from './types';
import { toDdMmYyyy } from './utils';

const formatDateForInput = (dateString: string | undefined): string => {
  if (!dateString) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
  if (/^\d{8}$/.test(dateString)) {
    const day = dateString.slice(0, 2);
    const month = dateString.slice(2, 4);
    const year = dateString.slice(4, 8);
    return `${year}-${month}-${day}`;
  }
  try {
    return new Date(dateString).toISOString().split('T')[0];
  } catch {
    return dateString;
  }
};

export interface ManualFormProps {
  config: DocumentImporterConfig;
  businessIdentifier: string;
  formDocument: ImporterDocumentRow;
  setFormDocument: React.Dispatch<React.SetStateAction<ImporterDocumentRow>>;
  editIndex: number | null;
  onAdd: (doc: ImporterDocumentRow) => void;
  onUpdate: (doc: ImporterDocumentRow) => void;
  onCancelEdit: () => void;
  existingFolios: string[];
}

const getFieldConfig = (
  key: ManualFormFieldConfig['key'],
  config: DocumentImporterConfig,
): ManualFormFieldConfig | undefined =>
  config.manualFormFields.find((f) => f.key === key);

const ManualForm: React.FC<ManualFormProps> = ({
  config,
  businessIdentifier,
  formDocument,
  setFormDocument,
  editIndex,
  onAdd,
  onUpdate,
  onCancelEdit,
  existingFolios,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const isEditing = editIndex !== null;

  const hasTypeField = getFieldConfig('type', config) != null;
  const typeOptions = config.documentTypeOptions ?? [];
  const typeField = getFieldConfig('type', config);
  const folioField = getFieldConfig('folio', config);
  const rutBusinessField = getFieldConfig('businessIdentifier', config);
  const rutStakeholderField = getFieldConfig('stakeholderIdentifier', config);
  const dateField = getFieldConfig('issuedDate', config);
  const amountField = getFieldConfig('amount', config);
  const stakeholderNameField = getFieldConfig('stakeholderName', config);

  const resolveBusinessName = async (
    rut: string,
  ): Promise<string | undefined> => {
    try {
      const res = await getBusinessNameByIdentifier(dispatch, {
        identifier: rut,
      });
      if (res?.name) return res.name;
    } catch {
      console.error('Error al resolver la razón social', rut);
    }
    return formDocument.stakeholderName ?? undefined;
  };

  const validate = (): boolean => {
    if (folioField?.required !== false && !formDocument.folio?.trim()) {
      showAlert(dispatch, 'error', 'Debes ingresar el folio');
      return false;
    }
    if (
      rutBusinessField?.required !== false &&
      !formDocument.businessIdentifier?.trim()
    ) {
      showAlert(dispatch, 'error', 'Debes ingresar el RUT emisor');
      return false;
    }
    if (!validaRut(formDocument.businessIdentifier)) {
      showAlert(dispatch, 'error', 'RUT emisor inválido');
      return false;
    }
    if (
      rutStakeholderField?.required !== false &&
      !formDocument.stakeholderIdentifier?.trim()
    ) {
      showAlert(dispatch, 'error', 'Debes ingresar el RUT deudor');
      return false;
    }
    if (!validaRut(formDocument.stakeholderIdentifier)) {
      showAlert(dispatch, 'error', 'RUT deudor inválido');
      return false;
    }
    if (dateField?.required !== false && !formDocument.issuedDate) {
      showAlert(dispatch, 'error', 'Debes ingresar la fecha de emisión');
      return false;
    }
    if (
      amountField?.required !== false &&
      (formDocument.amount == null || formDocument.amount <= 0)
    ) {
      showAlert(dispatch, 'error', 'El monto debe ser mayor a 0');
      return false;
    }
    if (!isEditing && existingFolios.includes(formDocument.folio.trim())) {
      showAlert(dispatch, 'error', 'Ya existe un documento con este folio');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const issuedDate =
        /^\d{4}-\d{2}-\d{2}$/.test(formDocument.issuedDate) ||
        !formDocument.issuedDate
          ? formDocument.issuedDate
          : formatDateForInput(formDocument.issuedDate);
      const doc: ImporterDocumentRow = {
        ...formDocument,
        businessIdentifier:
          formDocument.businessIdentifier ?? businessIdentifier,
        clientIdentifier: formDocument.clientIdentifier ?? businessIdentifier,
        issuedDate,
        id: formDocument.id ?? `${formDocument.folio}-${businessIdentifier}`,
      };
      const validateWithSIIAndThen = async (
        onSuccess: (finalDoc: ImporterDocumentRow) => void,
      ) => {
        try {
          const invoiceForSII = {
            ...doc,
            issuedDate: toDdMmYyyy(doc.issuedDate),
          };
          const response = await checkSIIDocumentForUploadingInvoices(
            dispatch,
            {
              invoices: [invoiceForSII],
            },
          );
          const result = response?.[0];
          if (result?.valid) {
            try {
              const nameResponse = await getBusinessNameByIdentifier(dispatch, {
                identifier: doc.stakeholderIdentifier,
              });
              onSuccess({ ...doc, stakeholderName: nameResponse?.name });
            } catch {
              onSuccess(doc);
            }
            setFormDocument(getEmptyDocument(businessIdentifier));
          } else {
            showAlert(
              dispatch,
              'error',
              result?.message ?? 'El documento no pasó la validación SII.',
            );
          }
        } catch (err) {
          console.error(err);
          showAlert(
            dispatch,
            'error',
            'Error al validar el documento con el SII.',
          );
        }
      };

      if (isEditing) {
        if (
          !config.validateWithSII ||
          doc.type === 'electronic-invoice-buyer'
        ) {
          let finalDoc = doc;
          if (stakeholderNameField && doc.stakeholderIdentifier) {
            finalDoc = {
              ...doc,
              stakeholderName: await resolveBusinessName(
                doc.stakeholderIdentifier,
              ),
            };
          }
          onUpdate(finalDoc);
          setFormDocument(getEmptyDocument(businessIdentifier));
          return;
        }
        await validateWithSIIAndThen(onUpdate);
        return;
      }
      if (!config.validateWithSII || doc.type === 'electronic-invoice-buyer') {
        let finalDoc = doc;
        if (stakeholderNameField && doc.stakeholderIdentifier) {
          finalDoc = {
            ...doc,
            stakeholderName: await resolveBusinessName(
              doc.stakeholderIdentifier,
            ),
          };
        }
        onAdd(finalDoc);
        setFormDocument(getEmptyDocument(businessIdentifier));
        return;
      }
      await validateWithSIIAndThen(onAdd);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    onCancelEdit();
    setFormDocument(getEmptyDocument(businessIdentifier));
  };

  return (
    <div>
      <Grid container spacing={3} alignItems="center">
        {hasTypeField && typeField && (
          <Grid item xs={12} sm={6}>
            <SelectInput<string>
              label={typeField.label}
              value={formDocument.type ?? null}
              onChange={(v) =>
                setFormDocument((prev) => ({ ...prev, type: v ?? undefined }))
              }
              options={typeOptions}
              fullWidth
            />
          </Grid>
        )}
        {folioField && (
          <Grid item xs={12} sm={6}>
            <TextInput
              label={folioField.label}
              placeholder={folioField.placeholder ?? 'ej. 001234'}
              value={formDocument.folio}
              onChange={(v) =>
                setFormDocument((prev) => ({ ...prev, folio: v ?? '' }))
              }
              fullWidth
            />
          </Grid>
        )}
        {rutBusinessField && (
          <Grid item xs={12} sm={6}>
            <TextInput
              label={rutBusinessField.label}
              placeholder={rutBusinessField.placeholder ?? '12.345.678-9'}
              value={formDocument.businessIdentifier}
              onChange={(v) =>
                setFormDocument((prev) => ({
                  ...prev,
                  businessIdentifier: v ?? '',
                }))
              }
              fullWidth
            />
          </Grid>
        )}
        {rutStakeholderField && (
          <Grid item xs={12} sm={6}>
            <TextInput
              label={rutStakeholderField.label}
              placeholder={rutStakeholderField.placeholder ?? '12.345.678-9'}
              value={formDocument.stakeholderIdentifier}
              onChange={(v) =>
                setFormDocument((prev) => ({
                  ...prev,
                  stakeholderIdentifier: v ?? '',
                }))
              }
              fullWidth
            />
          </Grid>
        )}
        {dateField && (
          <Grid item xs={12} sm={6}>
            <DateInput
              label={dateField.label}
              placeholder={dateField.placeholder ?? 'dd-mm-aaaa'}
              value={formatDateForInput(formDocument.issuedDate)}
              onChange={(v) =>
                setFormDocument((prev) => ({ ...prev, issuedDate: v }))
              }
              fullWidth
            />
          </Grid>
        )}
        {stakeholderNameField && (
          <Grid item xs={6}>
            <TextInput
              label={stakeholderNameField.label}
              placeholder={stakeholderNameField.placeholder}
              value={formDocument.stakeholderName ?? ''}
              onChange={(v) =>
                setFormDocument((prev) => ({
                  ...prev,
                  stakeholderName: v ?? undefined,
                }))
              }
              fullWidth
            />
          </Grid>
        )}
        {amountField && (
          <Grid item xs={12}>
            <MoneyInput
              label={amountField.label}
              value={formDocument.amount ?? null}
              onChange={(v) =>
                setFormDocument((prev) => ({ ...prev, amount: v ?? 0 }))
              }
              fullWidth
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {isEditing && (
              <CustomButton
                variant="outlined"
                color="secondary"
                onClick={handleCancelEdit}
                style={{ marginLeft: 8 }}
              >
                Cancelar
              </CustomButton>
            )}
            <CustomButton
              variant="contained"
              color="primary"
              startIcon={isEditing ? <Save /> : <Add />}
              onClick={() => void handleSubmit()}
              disabled={loading}
              loading={loading}
            >
              {isEditing ? 'Actualizar' : 'Agregar'}
            </CustomButton>
          </Box>
        </Grid>
      </Grid>
    </div>
  );
};

export function getEmptyDocument(
  businessIdentifier: string,
): ImporterDocumentRow {
  return {
    folio: '',
    businessIdentifier,
    stakeholderIdentifier: '',
    clientIdentifier: businessIdentifier,
    issuedDate: '',
    amount: 0,
  };
}

export default ManualForm;
